/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: BrandingPage.tsx
 ??? Created: 2025-01-06
 ?? Description:
    Main branding management page that lists available tenants
    and allows branding configuration for each tenant.

 ?? Notes:
    - Lists tenants available to the current user
    - If user has access to only one tenant, goes directly to form
    - Supports branding configuration for existing tenants
    - Integrates with tenant management system

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { Building2 } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useBrandingList } from './hooks/useBrandingList';
import { useBrandingColumns, useBrandingColumnRenderers } from './hooks/useBrandingColumns';
import { BrandingActions } from './components/BrandingActions';
import type { Tenant } from './api/brandingApi';

export default function BrandingPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentPageTitle } = useNavigationStore();

  // Clear page title to show tenant logo + name in header
  useEffect(() => {
    setCurrentPageTitle(null);
    
    return () => {
      setCurrentPageTitle(null);
    };
  }, [setCurrentPageTitle]);

  const availableColumns = useBrandingColumns();
  const columnRenderers = useBrandingColumnRenderers();

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle
  } = useBrandingList();

  const handleConfigureBranding = useCallback((tenant: Tenant) => {
    navigate(`/admin/system-settings/branding/${tenant.uuid ?? tenant.id}`);
  }, [navigate]);

  const renderActions = useCallback((tenant: Tenant) => (
    <BrandingActions tenant={tenant} onConfigure={handleConfigureBranding} />
  ), [handleConfigureBranding]);

  const redirectRef = useRef(false);
  const isListRoute = useMemo(
    () =>
      location.pathname === '/admin/system-settings/branding' &&
      location.state?.fromBranding !== true,
    [location.pathname, location.state]
  );

  useEffect(() => {
    if (!isListRoute) {
      redirectRef.current = false;
      return;
    }

    if (!loading && data && data.length === 1 && !redirectRef.current) {
      redirectRef.current = true;
      navigate(`/admin/system-settings/branding/${data[0].id}`, {
        replace: true,
        state: { fromBranding: true }
      });
    } else if (data && data.length !== 1) {
      redirectRef.current = false;
    }
  }, [data, isListRoute, loading, navigate]);

  return (
    <div className="p-6">
      <DataGridSimple
        title={t('branding.title')}
        titleIcon={<Building2 className="h-5 w-5 text-primary" />}
        data={data}
        columns={columns.map(col => ({
          ...col,
          render: columnRenderers[col.key as keyof typeof columnRenderers] as any,
        }))}
        onColumnToggle={handleColumnToggle}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        searchQuery={searchQuery}
        onSearch={handleSearch}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onEdit={handleConfigureBranding}
        searchPlaceholder={t('branding.searchTenants')}
        emptyMessage={t('branding.noTenantsFound')}
        renderActions={renderActions}
        translations={{
          items: t('common.items'),
          columns: t('common.columns'),
          noItemsFound: t('common.noItemsFound'),
          showing: t('common.showing'),
          to: t('common.to'),
          of: t('common.of'),
          results: t('common.results'),
          previous: t('common.previous'),
          next: t('common.next'),
          page: t('common.page'),
          deleteItem: t('common.deleteItem'),
          areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
          delete: t('common.delete'),
          cancel: t('common.cancel'),
          allSelected: t('common.allSelected'),
          selected: t('common.selected'),
          searchOptions: t('common.searchOptions'),
          noOptionsFound: t('common.noOptionsFound'),
          selectAll: t('common.selectAll'),
          clearAll: t('common.clearAll')
        }}
      />
    </div>
  );
}
